import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useCallback, useState } from "react";

import { EXPIRY_MS, HEADER_HEIGHT, TAB_BAR_HEIGHT, WARNING_THRESHOLD_MS } from "../constants";
import { useSessionTimer } from "../hooks/useSessionTimer";
import type { DropTarget } from "../hooks/useTabDrag";
import { useWindowInteractions } from "../hooks/useWindowInteractions";
import { useImpersonationSessions } from "../ImpersonationSessionsProvider";
import type { ImpersonationSession } from "../types";
import { DropZoneOverlays } from "./DropZoneOverlays";
import { SessionTab } from "./SessionTab";
import { SplitTabBar } from "./SplitTabBar";
import { TabGroupHeader } from "./TabGroupHeader";
import { TabGroupResizeHandle } from "./TabGroupResizeHandle";

interface TabbedImpersonationContainerProps {
  dockedSessions: ImpersonationSession[];
}

export function TabbedImpersonationContainer({
  dockedSessions,
}: TabbedImpersonationContainerProps) {
  const {
    activeTabId,
    closeSession,
    enterSplitView,
    exitSplitView,
    minimizeTabGroup,
    popOutSession,
    refreshSession,
    setActiveTab,
    splitLayout,
    tabGroupPosition,
    tabGroupSize,
    updateTabGroupPosition,
    updateTabGroupSize,
  } = useImpersonationSessions();

  const [showCloseAllDialog, setShowCloseAllDialog] = useState(false);
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    dropTarget: DropTarget;
  }>({ isDragging: false, dropTarget: null });

  const {
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,
  } = useWindowInteractions({
    sessionId: "__tab_group__",
    position: tabGroupPosition,
    size: tabGroupSize,
    updateWindowPosition: (_id, position) => updateTabGroupPosition(position),
    updateWindowSize: (_id, size) => updateTabGroupSize(size),
  });

  const elapsed = useSessionTimer(dockedSessions[0]?.startedAt ?? Date.now());
  const firstSession = dockedSessions[0];
  const remainingMs = firstSession?.expiresAt
    ? firstSession.expiresAt - Date.now()
    : EXPIRY_MS - elapsed;
  const isWarning = remainingMs <= WARNING_THRESHOLD_MS && remainingMs > 0;

  // Compute the drop zone rect for tab dragging (the iframe spacer area)
  const dropZoneRect =
    dockedSessions.length > 1
      ? {
          x: tabGroupPosition.x,
          y: tabGroupPosition.y + HEADER_HEIGHT + TAB_BAR_HEIGHT,
          width: tabGroupSize.width,
          height: tabGroupSize.height,
        }
      : null;

  const closeAllDockedSessions = () => {
    for (const session of dockedSessions) {
      closeSession(session.id);
    }
  };

  const handleCloseAll = () => {
    if (dockedSessions.length > 1) {
      setShowCloseAllDialog(true);
    } else {
      closeAllDockedSessions();
    }
  };

  const handleDragToSplit = useCallback(
    (sessionId: string, side: "left" | "right") => {
      const currentActive = activeTabId ?? dockedSessions[0]?.id;
      const otherId =
        !currentActive || currentActive === sessionId
          ? dockedSessions.find((s) => s.id !== sessionId)?.id
          : currentActive;
      if (!otherId) {
        return;
      }

      const [left, right] = side === "left" ? [sessionId, otherId] : [otherId, sessionId];
      enterSplitView(left, right);
    },
    [activeTabId, dockedSessions, enterSplitView]
  );

  const anyTabDragging = dragState.isDragging;

  return (
    <>
      <Box
        sx={(theme) => ({
          position: "fixed",
          left: tabGroupPosition.x,
          top: tabGroupPosition.y,
          zIndex: theme.zIndex.modal + 50,
          display: "flex",
          flexDirection: "column",
          userSelect: "none",
          boxShadow: 24,
          borderRadius: "8px 8px 4px 4px",
          overflow: "hidden",
        })}
      >
        {/* Draggable header */}
        <TabGroupHeader
          dockedSessions={dockedSessions}
          isWarning={isWarning}
          width={tabGroupSize.width}
          splitLayout={splitLayout}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onExitSplitView={exitSplitView}
          onPopOutSession={popOutSession}
          onMinimizeTabGroup={minimizeTabGroup}
          onCloseAll={handleCloseAll}
        />

        {/* Tab bar */}
        <Box
          sx={{
            display: "flex",
            gap: 0.25,
            backgroundColor: "grey.400",
            paddingX: 0.5,
            paddingTop: 0.5,
            width: tabGroupSize.width,
            boxSizing: "border-box",
            overflowX: "auto",
            alignItems: "flex-end",
          }}
        >
          {splitLayout ? (
            <SplitTabBar
              dockedSessions={dockedSessions}
              splitLayout={splitLayout}
              dropZoneRect={dropZoneRect}
              onSelect={setActiveTab}
              onRefresh={refreshSession}
              onPopOut={popOutSession}
              onClose={closeSession}
              onDragToSplit={handleDragToSplit}
              onDragOut={popOutSession}
              onExitSplit={exitSplitView}
              onDragStateChange={(d, t) => {
                setDragState({ isDragging: d, dropTarget: t });
              }}
            />
          ) : (
            dockedSessions.map((session) => {
              const { id } = session;
              return (
                <SessionTab
                  key={id}
                  session={session}
                  isActive={id === activeTabId}
                  dropZoneRect={dropZoneRect}
                  onSelect={() => setActiveTab(id)}
                  onRefresh={() => refreshSession(id)}
                  onPopOut={() => popOutSession(id)}
                  onClose={() => closeSession(id)}
                  onDragToSplit={(side) => handleDragToSplit(id, side)}
                  onDragOut={() => popOutSession(id)}
                  onDragStateChange={(d, t) => setDragState({ isDragging: d, dropTarget: t })}
                />
              );
            })
          )}
        </Box>

        {/* Spacer for the iframe area + resize handle (iframes rendered by overlay) */}
        <Box
          sx={{
            width: tabGroupSize.width,
            height: tabGroupSize.height,
            position: "relative",
            boxSizing: "border-box",
            pointerEvents: "none",
          }}
        >
          <DropZoneOverlays
            anyTabDragging={anyTabDragging}
            dropTarget={dragState.dropTarget}
            splitLayout={splitLayout}
          />
        </Box>
      </Box>
      <TabGroupResizeHandle
        tabGroupPosition={tabGroupPosition}
        tabGroupSize={tabGroupSize}
        onResizeStart={handleResizeStart}
        onResizeMove={handleResizeMove}
        onResizeEnd={handleResizeEnd}
      />

      {/* Close all confirmation dialog */}
      <Dialog
        open={showCloseAllDialog}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 300 }}
        onClose={() => setShowCloseAllDialog(false)}
      >
        <DialogTitle>End all docked sessions?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will end {dockedSessions.length} impersonation sessions in the group pane. Sessions
            outside the pane will not be affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloseAllDialog(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setShowCloseAllDialog(false);
              closeAllDockedSessions();
            }}
          >
            End all sessions
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
